<template>
    <!--h4 class="text-success text-center">Api integrated</h4-->
    <div class="mb-3" v-if="!props.personId">
        <BreadcrumbNav />
    </div>
    <div class="container-fluid">
        <!-- Tab Legend -->
        <div v-if="!props.scheduleManagementOnly" class="d-flex align-items-center gap-3 mb-2 text-xs text-secondary">
            <span><i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i> Required</span>
            <span><i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i> Optional</span>
        </div>

        <p v-if="props.scheduleManagementOnly" class="alert alert-info py-2">
            This limited editor manages only the member’s availability, serving preferences, and scheduling rules for this People Group.
        </p>

        <NavTabs>
            <NavTab v-if="!props.scheduleManagementOnly" target="user-details" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="User Details (Required)">
                    Details <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>

            <NavTab target="availability" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Availability (Optional)">
                    Availability <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab target="preferred-activities" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Preferred Activities and Workgroups (Optional)">
                    Preferences <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab target="schedule-preferences" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="User Scheduling Rules (Optional)">
                    User Rules <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab v-if="!props.scheduleManagementOnly" target="people-groups" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="People Group memberships">
                    People Groups <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab v-if="!props.scheduleManagementOnly" target="assigned-skills" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Current Assigned Skills (Required)">
                    Skills <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>

            <NavTab v-if="!props.scheduleManagementOnly" target="admin-groups" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Admin Groups (Optional)">
                    Admin Groups <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab v-if="!props.scheduleManagementOnly" target="available-workgroups" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Available Workgroups' Teams (Optional)">
                    Teams <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab v-if="!props.scheduleManagementOnly" target="notifications" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Notification Preferences (Optional)">
                    Notifications <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab v-if="!props.scheduleManagementOnly" target="interested-skills" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Interest Skills (Optional)">
                    Interests <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab v-if="!props.scheduleManagementOnly" target="user-assignments" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="User Assignments (Optional)">
                    Assignments <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab v-if="canManageAccountDeletion && !props.scheduleManagementOnly" target="account-deletion" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Account deletion and location actions">
                    Account <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">warning</i>
                </span>
            </NavTab>
        </NavTabs>

        <ContentContainer>
            <template #content>
                <ExplainPageButton :page-key="`user.${activeTab}`" />
                <div v-if="activeTab === 'user-details'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and edit the user's profile information, contact details, and account settings.</p>
                    <UserDetails />
                </div>

                <div v-else-if="activeTab === 'admin-groups'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Manage the administrative groups this user belongs to.</p>
                    <AdminGroups :targetUserInfo="targetUserInfo" />
                </div>

                <div v-else-if="activeTab === 'availability'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Set the dates and times when this user is available or unavailable for scheduling.</p>
                    <Availability />
                </div>

                <div v-else-if="activeTab === 'preferred-activities'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Manage the activities and workgroups this user prefers to be scheduled for.</p>
                    <PreferredActivities :targetUserInfo="targetUserInfo" :group-admin-edit="props.scheduleManagementOnly" />
                </div>

                <div v-else-if="activeTab === 'available-workgroups'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and manage the workgroup teams available to this user.</p>
                    <AvailableWorkgroups />
                </div>

                <div v-else-if="activeTab === 'notifications'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Configure how and when this user receives notifications.</p>
                    <NotificationsPreferences />
                </div>

                <div v-else-if="activeTab === 'schedule-preferences'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Define scheduling rules and constraints for this user.</p>
                    <SchedulePreferences :targetUserInfo="targetUserInfo" :group-admin-edit="props.scheduleManagementOnly" />
                </div>

                <div v-else-if="activeTab === 'people-groups'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Manage this user's People Group memberships. Group admins still need each member's consent before managing their schedule or availability.</p>
                    <PeopleGroupsPanel :target-user-id="effectivePersonId" />
                </div>

                <div v-else-if="activeTab === 'assigned-skills'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and manage the skills currently assigned to this user.</p>
                    <CurrentAssignedSkills />
                </div>

                <div v-else-if="activeTab === 'interested-skills'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and manage skills this user has expressed interest in.</p>
                    <InterestedSkills />
                </div>

                <div v-else-if="activeTab === 'user-assignments'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View the scheduling assignments for this user.</p>
                    <UserAssignments />
                </div>

                <div v-else-if="activeTab === 'account-deletion' && canManageAccountDeletion">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">
                        Review the impact before removing this user's membership or applying a location-scoped account action.
                    </p>
                    <AccountDeletion
                        :key="`${effectivePersonId}:${currentLocationId}`"
                        :person-id="effectivePersonId"
                        :person-name="targetUserName"
                        :location-id="currentLocationId"
                        @completed="handleAccountActionCompleted"
                    />
                </div>
            </template>
        </ContentContainer>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, provide, toRef } from "vue"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import NavTab from "../../components/Tabs/NavTab.vue"
import NavTabs from "../../components/Tabs/NavTabs.vue"
import ContentContainer from "../../components/PageLayout/ContentContainer.vue"
import ExplainPageButton from "@/components/common/ExplainPageButton.vue"
import UserDetails from "./components/UserDetails.vue"
import AdminGroups from "./components/AdminGroups.vue"
import Availability from "./components/Availability.vue"
import PreferredActivities from "./components/PreferredActivities.vue"
import AvailableWorkgroups from "./components/AvailableWorkgroups.vue"
import NotificationsPreferences from "./components/NotificationsPreferences.vue"
import SchedulePreferences from "./components/SchedulePreferences.vue"
import PeopleGroupsPanel from "@/modules/people-groups/PeopleGroupsPanel.vue"
import CurrentAssignedSkills from "./components/CurrentAssignedSkills.vue"
import InterestedSkills from "./components/InterestedSkills.vue"
import UserAssignments from "./components/UserAssignments.vue"
import AccountDeletion from "./components/AccountDeletion.vue"
import BreadcrumbNav from "../../components/common/BreadcrumbNav.vue"
import { useBreadcrumb } from "@/composables/useBreadcrumb"
import { getLocationId } from "../../utils/SessionDataHandler"
import { usePermissions } from "@/composables/usePermissions"

const props = defineProps({
    personId: { type: [Number, String], default: null },
    initialActiveTab: { type: String, default: null },
    scheduleManagementOnly: { type: Boolean, default: false },
    peopleGroupId: { type: [Number, String], default: null },
})
const emit = defineEmits(["account-updated"])

const store = useStore()
const route = useRoute()
const router = useRouter()
const requestedTab = props.initialActiveTab || route.query.tab
const allowedTabs = props.scheduleManagementOnly
    ? ["availability", "preferred-activities", "schedule-preferences"]
    : ["user-details", "availability", "preferred-activities", "schedule-preferences", "available-workgroups"]
const initialTab = allowedTabs
    .includes(typeof requestedTab === "string" ? requestedTab : "")
    ? requestedTab
    : props.scheduleManagementOnly ? "availability" : "user-details"
const activeTab = ref(initialTab)
const { trail, setTrail } = useBreadcrumb()
const currentLocationId = getLocationId()
const {
    isExactlyActivityInstanceAdmin,
    isExactlyActivityInstanceWorkgroupAdmin,
} = usePermissions()

// Effective personId: prefer prop (modal mode) over route param
const effectivePersonId = computed(() =>
    props.personId ? Number(props.personId) : Number(route.params.userid)
)
provide('editUserPersonId', effectivePersonId)
provide('editUserPeopleGroupId', toRef(props, 'peopleGroupId'))
const canManageAccountDeletion = computed(() => (
    !props.scheduleManagementOnly
    &&
    !isExactlyActivityInstanceAdmin.value
    && !isExactlyActivityInstanceWorkgroupAdmin.value
))

// Target user info shared with permission-aware child components
const targetUserInfo = ref(null)
const targetUserName = ref("")

const loadTargetUserInfo = async () => {
    if (props.scheduleManagementOnly) return
    const personId = effectivePersonId.value
    if (!personId) return
    try {
        const details = await store.dispatch("fetchUserDetails", {
            locationId: currentLocationId,
            personId,
        })
        const userData = details?.output?.[0]
        if (userData) {
            targetUserInfo.value = {
                skill_ids: userData.skill_ids || [],
                is_organization_admin: !!userData.is_organization_admin,
                is_location_admin: !!userData.is_location_admin,
                is_activity_admin: !!userData.is_activity_admin,
            }
            targetUserName.value = `${userData.firstname || ""} ${userData.lastname || ""}`.trim()
            // Fallback trail for direct-URL access
            if (!trail.value.length) {
                setTrail([
                    { label: 'Users', icon: 'people', route: { name: 'Users' } },
                    { label: targetUserName.value || `User #${personId}`, icon: 'person' },
                ])
            }
        }
    } catch (e) {
        console.error("Failed to load target user info for permission checks:", e)
    }
}

onMounted(() => {
    if (!props.scheduleManagementOnly) loadTargetUserInfo()
})

const handleAccountActionCompleted = async (result) => {
    const shouldLeaveEditor = !!(result?.shouldLeaveEditor || result?.data?.account_deleted)
    if (!shouldLeaveEditor) {
        await loadTargetUserInfo()
    }

    emit("account-updated", { ...result, shouldLeaveEditor })
    if (shouldLeaveEditor && !props.personId) {
        await router.replace({ name: "Users" })
    }
}
</script>
